import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FormInput,
  FormRoot,
  RHF,
  toast,
  zodResolver,
} from "@devhop/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { CheckCircle2, Loader2, Pencil } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { z } from "zod";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { profileApi } from "@/modules/auth/presentation/api/client";
import { useAuthState } from "@/modules/auth/presentation/model/useAuthState";
import type { EmployeeProfileResponseDTO } from "@/modules/users/domain/contracts/employee-profile";
import { formatDateLocal } from "@/shared/lib/date-time";
import { uploadAvatarFile } from "@/shared/lib/upload-avatar";
import { AppImage, resolveImageSrc } from "@/shared/ui/AppImage";
import { AvatarDeferredUpload } from "@/shared/ui/AvatarDeferredUpload";
import { RichTextBlock } from "@/shared/ui/RichTextOrPlain";
import { dashboardKeys, useEmployeeProfileQuery } from "../api/queries";

function formatJoinDateDisplay(iso: string | null | undefined): string {
  if (iso == null || iso === "") return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return formatDateLocal(d);
}

/** Stable-enough React key for JD lines (no id from API). */
function jdLineKey(prefix: string, index: number, text: string): string {
  let h = 0;
  for (let i = 0; i < text.length; i++) {
    h = (Math.imul(31, h) + text.charCodeAt(i)) | 0;
  }
  return `${prefix}-${index}-${(h >>> 0).toString(36)}`;
}

const JD_ROUTINE_FREQUENCY_LABEL: Record<
  EmployeeProfileResponseDTO["job"]["routineTasks"][number]["frequency"],
  string
> = {
  daily: "ປະຈຳວັນ",
  weekly: "ປະຈຳອາທິດ",
  monthly: "ປະຈຳເດືອນ",
  quarterly: "ປະຈຳໄຕມາດ",
  annual: "ປະຈຳປີ",
};

function jdStatusLabel(
  s: EmployeeProfileResponseDTO["summary"]["jdStatus"],
): string {
  if (s === "published") return "ຢືນຢັນແລ້ວ";
  if (s === "draft") return "ຮ່າງ";
  if (s === "archived") return "ເກັບຖາວອນ";
  return "—";
}

function InfoField({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="min-w-0">
      <p className="text-muted-foreground text-xs">{label}</p>
      <p className="wrap-break-word font-medium text-foreground text-sm">
        {value == null || value === "" ? "—" : value}
      </p>
    </div>
  );
}

const ContactEditSchema = z.object({
  name: z.string().min(1, "ຕ້ອງໃສ່ຊື່"),
  phoneNumber: z.string().optional(),
  image: z.string().optional().nullable(),
  imageFile: z.instanceof(File).optional().nullable(),
});

type ContactEditValues = z.infer<typeof ContactEditSchema>;

function ProfileContactEditor({
  open,
  onOpenChange,
  person,
  submitting,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  person: EmployeeProfileResponseDTO["person"];
  submitting: boolean;
  onSubmit: (vals: {
    name: string;
    phoneNumber?: string;
    image?: string | null;
    imageFile?: File | null;
  }) => Promise<void>;
}) {
  const navigate = useNavigate();
  const methods = RHF.useForm<ContactEditValues>({
    resolver: zodResolver(ContactEditSchema),
    defaultValues: {
      name: person.name ?? "",
      phoneNumber: person.phoneNumber ?? "",
      image: person.image ?? undefined,
      imageFile: undefined,
    },
  });

  useEffect(() => {
    if (!open) return;
    methods.reset({
      name: person.name ?? "",
      phoneNumber: person.phoneNumber ?? "",
      image: person.image ?? undefined,
      imageFile: undefined,
    });
  }, [open, person, methods]);

  if (!open) return null;

  return (
    <Card className="mb-6 border-dashed">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base">ແກ້ໄຂຂໍ້ມູນຕິດຕໍ່</CardTitle>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            ປິດ
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <FormRoot<ContactEditValues>
          methods={methods}
          onSubmit={async (vals) => {
            await onSubmit({
              name: vals.name,
              phoneNumber: vals.phoneNumber || undefined,
              image: vals.image ?? undefined,
              imageFile: vals.imageFile ?? undefined,
            });
          }}
          className="space-y-4"
        >
          <RHF.Controller
            name="image"
            control={methods.control}
            render={({ field: imageField }) => (
              <RHF.Controller
                name="imageFile"
                control={methods.control}
                render={({ field: fileField }) => (
                  <AvatarDeferredUpload
                    value={
                      imageField.value
                        ? resolveImageSrc(imageField.value)
                        : undefined
                    }
                    imageFile={fileField.value ?? undefined}
                    onChange={imageField.onChange}
                    onFileSelect={fileField.onChange}
                    hint="ເລືອກຮູບ ຈະອັບໂຫຼດເມື່ອກົດບັນທຶກ"
                  />
                )}
              />
            )}
          />
          <FormInput name="name" label="ຊື່" requiredMark />
          <FormInput name="phoneNumber" label="ໂທລະສັບ" placeholder="+856 …" />
          <div className="flex flex-wrap gap-2">
            <Button type="submit" isLoading={submitting}>
              ບັນທຶກ
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => void navigate({ to: "/app/profile" })}
            >
              ປ່ຽນລະຫັດຜ່ານ
            </Button>
          </div>
        </FormRoot>
      </CardContent>
    </Card>
  );
}

export function DashboardPage() {
  const { user, refetch: refetchSession } = useAuthState();
  const profileQuery = useEmployeeProfileQuery();
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const saveContact = useCallback(
    async (vals: {
      name: string;
      phoneNumber?: string;
      image?: string | null;
      imageFile?: File | null;
    }) => {
      setSubmitting(true);
      try {
        let imageKey: string | null | undefined = vals.image ?? undefined;
        if (vals.imageFile instanceof File) {
          imageKey = await uploadAvatarFile(vals.imageFile);
        }
        const fd = new FormData();
        fd.append("name", vals.name);
        fd.append("phoneNumber", vals.phoneNumber ?? "");
        if (vals.image === null && !vals.imageFile) {
          fd.append("imageDelete", "1");
        } else if (typeof imageKey === "string" && imageKey) {
          fd.append("image", imageKey);
        }
        await profileApi.update(fd);
        toast.success("ບັນທຶກຂໍ້ມູນສຳເລັດ");
        await queryClient.invalidateQueries({
          queryKey: dashboardKeys.employeeProfile,
        });
        refetchSession();
        setEditOpen(false);
      } finally {
        setSubmitting(false);
      }
    },
    [queryClient, refetchSession],
  );

  if (!user?.id) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex min-h-[40vh] items-center justify-center gap-2 text-muted-foreground">
            <Loader2 className="size-6 animate-spin" />
            <span>ກຳລັງໂຫຼດຂໍ້ມູນ…</span>
          </div>
        </Main>
      </>
    );
  }

  if (profileQuery.isPending) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex min-h-[40vh] items-center justify-center gap-2 text-muted-foreground">
            <Loader2 className="size-6 animate-spin" />
            <span>ກຳລັງໂຫຼດຂໍ້ມູນ…</span>
          </div>
        </Main>
      </>
    );
  }

  if (!profileQuery.data) {
    return (
      <>
        <Header />
        <Main>
          <p className="text-muted-foreground">ບໍ່ສາມາດໂຫຼດຂໍ້ມູນໂປຣໄຟລ໌ໄດ້.</p>
        </Main>
      </>
    );
  }

  const p = profileQuery.data;

  return (
    <>
      <Header />
      <Main>
        <div className="mb-6">
          <h1 className="font-bold text-2xl tracking-tight">ຂໍ້ມູນສ່ວນຕົວ</h1>
          <p className="mt-1 text-muted-foreground">ສູນບໍລິການຕົນເອງສຳລັບພະນັກງານ</p>
        </div>

        <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardContent className="">
              <p className="text-muted-foreground text-xs">ຕຳແໜ່ງ</p>
              <p className="mt-1 font-semibold text-foreground">
                {p.summary.positionTitle ?? "—"}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-muted-foreground text-xs">ສະຖານະ JD</p>
                <p className="mt-1 flex items-center gap-1.5 font-semibold text-foreground">
                  {p.summary.jdStatus === "published" ? (
                    <CheckCircle2
                      className="size-4 shrink-0 text-emerald-600"
                      aria-hidden
                    />
                  ) : null}
                  {jdStatusLabel(p.summary.jdStatus)}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="">
              <p className="text-muted-foreground text-xs">ຜູ້ຈັດການ</p>
              <p className="mt-1 font-semibold text-foreground">
                {p.summary.managerName ?? "—"}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="">
              <p className="text-muted-foreground text-xs">ເສັ້ນທາງອາຊີບ</p>
              <p className="mt-1 font-semibold text-foreground">
                {p.summary.careerLevelName ?? "—"}
              </p>
            </CardContent>
          </Card>
        </div>

        <ProfileContactEditor
          open={editOpen}
          onOpenChange={setEditOpen}
          person={p.person}
          submitting={submitting}
          onSubmit={saveContact}
        />

        <div className="grid gap-6 lg:grid-cols-12">
          <div className="space-y-6 lg:col-span-8">
            <Card>
              <CardContent className="">
                <div className="flex flex-col gap-6 sm:flex-row">
                  <div className="relative shrink-0">
                    <div className="relative size-36 overflow-hidden rounded-lg border bg-muted">
                      <AppImage
                        src={p.person.image}
                        alt=""
                        className="size-full"
                      />
                    </div>
                    <Button
                      className="absolute -right-1 -bottom-1 flex size-9 items-center justify-center rounded-full border bg-background shadow-sm hover:bg-muted"
                      onClick={() => setEditOpen(true)}
                      aria-label="ແກ້ໄຂຮູບໂປຣໄຟລ໌"
                    >
                      <Pencil className="size-4 text-black dark:text-white" />
                    </Button>
                  </div>
                  <div className="min-w-0 flex-1 space-y-4">
                    <h2 className="font-semibold text-lg">ຂໍ້ມູນສ່ວນຕົວ</h2>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <InfoField label="ຜະແນກ" value={p.departmentName} />
                      <InfoField label="ໂທລະສັບ" value={p.person.phoneNumber} />
                      <InfoField
                        label="ວັນທີເຂົ້າວຽກ"
                        value={formatJoinDateDisplay(p.person.joinDate)}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setEditOpen(true)}
                    >
                      ແກ້ໄຂຂໍ້ມູນຕິດຕໍ່
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>ລາຍລະອຽດວຽກ (JD)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {p.job.responsibilities.length === 0 &&
                p.job.qualifications.length === 0 &&
                p.job.routineTasks.length === 0 ? (
                  <p className="text-muted-foreground text-sm">
                    ບໍ່ມີລາຍລະອຽດ JD ທີ່ເຜີຍແຜ່ສຳລັບຕຳແໜ່ງຂອງທ່ານ.
                  </p>
                ) : (
                  <>
                    {p.job.responsibilities.length > 0 ? (
                      <div>
                        <p className="mb-2 font-medium text-sm">
                          ຄວາມຮັບຜິດຊອບຫຼັກ
                        </p>
                        <div className="space-y-3 text-sm">
                          {p.job.responsibilities.map((line, i) => (
                            <RichTextBlock
                              key={jdLineKey("resp", i, line)}
                              text={line}
                            />
                          ))}
                        </div>
                      </div>
                    ) : null}
                    {p.job.routineTasks.length > 0 ? (
                      <div>
                        <p className="mb-2 font-medium text-sm">ວຽກປະຈຳ</p>
                        <div className="space-y-4 text-sm">
                          {p.job.routineTasks.map((task, i) => (
                            <div
                              key={jdLineKey(
                                "routine",
                                i,
                                `${task.frequency}:${task.description}`,
                              )}
                            >
                              <p className="mb-1.5 font-medium text-muted-foreground text-xs uppercase tracking-wide">
                                {JD_ROUTINE_FREQUENCY_LABEL[task.frequency]}
                              </p>
                              <RichTextBlock text={task.description} />
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                    {p.job.qualifications.length > 0 ? (
                      <div>
                        <p className="mb-2 font-medium text-sm">ຄຸນສົມບັດ</p>
                        <div className="space-y-3 text-sm">
                          {p.job.qualifications.map((line, i) => (
                            <RichTextBlock
                              key={jdLineKey("qual", i, line)}
                              text={line}
                            />
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6 lg:col-span-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">ສາຍງານ</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="mb-2 text-muted-foreground text-xs">
                    ຫົວໜ້າໂດຍກົງ
                  </p>
                  {p.manager?.id ? (
                    <div className="flex items-center gap-3 rounded-lg border p-3">
                      <div className="size-11 shrink-0 overflow-hidden rounded-full bg-muted">
                        <AppImage
                          src={p.manager.image}
                          alt=""
                          className="size-full"
                        />
                      </div>
                      <div className="min-w-0">
                        <p className="truncate font-medium text-sm">
                          {p.manager.name ?? "—"}
                        </p>
                        <p className="truncate text-muted-foreground text-xs">
                          {p.manager.positionTitle ?? "—"}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-sm">—</p>
                  )}
                </div>
                <div>
                  <p className="mb-2 text-muted-foreground text-xs">ສະມາຊິກທີມ</p>
                  {p.team.length === 0 ? (
                    <p className="text-muted-foreground text-sm">—</p>
                  ) : (
                    <ul className="space-y-2">
                      {p.team.map((m) => (
                        <li
                          key={m.id}
                          className="flex items-center gap-2 rounded-md border border-transparent px-1 py-1 hover:border-border"
                        >
                          <div className="size-9 shrink-0 overflow-hidden rounded-full bg-muted">
                            <AppImage
                              src={m.image}
                              alt=""
                              className="size-full"
                            />
                          </div>
                          <div className="min-w-0">
                            <p className="truncate font-medium text-sm">
                              {m.name ?? "—"}
                            </p>
                            <p className="truncate text-muted-foreground text-xs">
                              {m.positionTitle ?? "—"}
                            </p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">ເສັ້ນທາງອາຊີບ</CardTitle>
              </CardHeader>
              <CardContent>
                {p.careerPath.length === 0 ? (
                  <p className="text-muted-foreground text-sm">
                    ບໍ່ມີຂໍ້ມູນລະດັບຕຳແໜ່ງ.
                  </p>
                ) : (
                  <ul className="space-y-3">
                    {p.careerPath.map((step) => (
                      <li key={step.id} className="flex gap-3">
                        <div
                          className={`mt-1.5 size-2.5 shrink-0 rounded-sm ${
                            step.phase === "current"
                              ? "bg-primary ring-2 ring-primary/30"
                              : step.phase === "past"
                                ? "bg-muted-foreground/40"
                                : "bg-muted-foreground/20"
                          }`}
                          aria-hidden
                        />
                        <div
                          className={
                            step.phase === "past"
                              ? "text-muted-foreground"
                              : undefined
                          }
                        >
                          <p className="font-medium text-sm">{step.name}</p>
                          <p className="text-muted-foreground text-xs">
                            {step.phase === "current"
                              ? "ປັດຈຸບັນ"
                              : step.phase === "past"
                                ? "ຜ່ານມາ"
                                : "ຕໍ່ໄປ"}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </Main>
    </>
  );
}
